/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { decamelize } from 'humps';
import { useTranslation } from 'react-i18next';
import { Gap, Icon, ResultBox, Text, Visibility } from '@reservamos/elements';
import FlightItem from 'components/search/FlightItem';
import MixedItem from 'components/search/MixedItem';
import ResultDefault from 'components/search/ResultDefault';
import SimpleTripResult from 'components/search/SimpleTripResult';
import TripResult from 'components/search/TripResult';
import ResultSpecifics from 'components/search/ResultSpecifics';
import ResultBoxHorizontal from 'components/search/ResultBoxHorizontal';
import dotersDot from 'images/doters/greendoters.svg';
import { useSelector } from 'react-redux';
import SeatSelectorFactory from 'components/seats/SeatSelectorFactory';
import { useHistory } from 'react-router-dom';
import { hasDisplayTotal } from 'utils/trips/displayedPrice';
import { usePriceWithoutFeesVariant } from 'utils/trips/priceWithoutFeesExperiment';
import { finalPrice, hasMarkupAboveTotal } from 'utils/trips/finalPricing';
import { PRICE_ARMS, resolvePriceArm } from 'utils/trips/priceArm';
import reportsFinalPrice from 'utils/trips/reportsFinalPrice';
import useFinalPriceExperiment from 'hooks/useFinalPriceExperiment';
import {
  resolveTripItemPrices,
  selectTripItemCandidate,
  selectTripItemPricing,
} from 'utils/search/tripItemPricing';
import { getProductType } from 'utils/Reserbus';
import { isSeatsOnResultActivated } from 'utils/seats';
import {
  checkRecentTripSeen,
  cleanPrevRecentSeenTrip,
  deleteExpiredRecentSeenTrips,
  saveCacheRecentSeenTrip,
} from 'utils/trips/cacheTrip';
import fingerprintService from 'services/userFingerprint/userFingerprint';
import { quickPurchaseSelectedTracker } from 'metrics/user-analytics/purchase';
import useWhitelabelTheme from '../../hooks/whitelabel/useWhitelabelTheme';
import useSearch from '../../hooks/store/useSearch';
import ResultWithIcons from './ResultWithIcons';
import { flatFareDiscountTotal } from '../../utils/doters';
import 'styles/components/search/ResultBox';
import ResultGfa from './ResultGfa';
import useSearchCouponSelection from './SearchCoupon/SearchCoupon.hooks';
import '../../styles/components/search/TripItem';
import { useBusDiagramEnabled } from '../../utils/personalizationAbTest';
import {
  getPassengerTypesForPricing,
  shouldShowProviderDiscountForSearch,
} from '../../utils/passengerCategories';
import useGrowthBookFeatureValue from '../GrowthBookProvider/useGrowthBookFeatureValue';
import useExperimentalFeature from '../../hooks/useExperimentalFeature';
import useUserPreferences from '../../hooks/useUserPreferences';
import {
  getBaseEligibility,
  getTripEligibility,
  getInSeatsEligibility,
} from '../../utils/quickPurchaseEligibility';
import useLoyaltyPrograms from '../../loyalty/context/useLoyaltyPrograms';
import buildQuickPurchasePath, { pushQuickPurchasePath } from '../../utils/quickPurchaseUrl';

/**
 * TripItem component.
 * Renders a trip item in the search results list.
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered TripItem component.
 */
const TripItem = (props) => {
  const { t } = useTranslation();
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const { userIsLoggedInWithAnyLoyaltyProgram, isQuickPurchaseLoyaltySettled = true } =
    useLoyaltyPrograms();
  const {
    resultPriceToShow,
    passengers: searchPassengers,
    departure: searchDeparture,
    couponCode,
  } = useSearch();
  const isBulkTicket = searchDeparture?.routeType === 'bulk_ticket';
  const { resultBoxStyle } = useWhitelabelTheme();
  const isNewResultsDesign = features.NEW_RESULTS_DESIGN;

  // Check if bus diagram should be enabled based on the personalization feature flag
  const busDiagramEnabled = useBusDiagramEnabled();

  // AB Test: Enable pricing based on searched passenger categories
  const categoryBasedPricing = useGrowthBookFeatureValue('category_based_pricing');
  // Fall back to half-price categories (older_half, etc.) before general
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');

  const quickPurchaseEnabled = useExperimentalFeature('QUICK_PURCHASE_ENABLED');
  const quickCheckoutVariant = useGrowthBookFeatureValue('quick_checkout_variant');

  const { couponLoading } = useSearchCouponSelection();
  const { passengers: frequentPassengers } = useUserPreferences();

  const [showBusDiagram, setShowBusDiagram] = useState(false);

  const history = useHistory();
  const isDesktop = getProductType() === 'desktop';

  const {
    trip,
    type,
    position,
    selectTrip,
    id,
    isFromOpenTicketList,
    filtersApplied,
    isExchange,
    availability,
    isRoundTrip,
    way,
    isLogged,
    departureTrip,
    rewardTicketStatus,
    setRewardTicketStatus,
    highLightAsMobile,
    allowsSeatSelection,
    exchangePassengers,
    isClosed, // Indicates if this trip is closed/unavailable (sold out or departed)
    isTerminalGrouped,
    onQuickPurchaseRequest,
    pricing: listPricing,
  } = props;

  // Prepare exchange data if available
  const fromExchange =
    isExchange && exchangePassengers
      ? {
          passengers: exchangePassengers,
        }
      : null;

  // Check both if seats on results are enabled by config and by personalization feature flag
  const isSeatsOnResults = isSeatsOnResultActivated() && allowsSeatSelection && busDiagramEnabled;

  /**
   * useEffect hook that cleans up previous recently seen trips when the component unmounts.
   */
  useEffect(() => {
    return () => {
      cleanPrevRecentSeenTrip();
    };
  }, []);

  /**
   * Checks if the trip has an error when fetching the details to close it
   */

  let content;

  const hasFilters =
    !isExchange &&
    (!filtersApplied.departureTime?.includes('none') ||
      filtersApplied.categories?.length ||
      filtersApplied.tripOptions?.length ||
      filtersApplied.reclineOptions?.length ||
      !filtersApplied.stops?.includes('none'));

  const showNewFiltersList = features.NEW_FILTERS_VERSION && hasFilters && !isExchange;
  const { passengerTypes } = trip;
  // The list resolves coupon/wallet and validated-return pricing before rendering this
  // card. Direct callers retain the existing leg rule and round-trip validation.
  const pricingKey = selectTripItemPricing({
    trip,
    isRoundTrip,
    way,
    departureTrip,
    validateRoundTripDiscount: features.VALIDATE_ROUND_TRIP_DISCOUNT,
    listPricing,
  });

  const { walletPoints, discountBase } = pricingKey;

  const { flatFareValid } = flatFareDiscountTotal({
    way,
    trip,
    isRoundTrip,
    departureTrip,
    isFlatFareEnabled: features.FLAT_FARE_ENABLED,
  });
  const showFlatFare = flatFareValid === 'valid';
  const isRecentlySeen = checkRecentTripSeen(trip.id);

  const isNewUserForDigitalDiscount = fingerprintService.isFirstSession();

  // Get passenger types to consider for minimum price calculation
  const typesToFilter = getPassengerTypesForPricing(
    searchPassengers,
    passengerTypes,
    Boolean(categoryBasedPricing),
    Boolean(halfPriceCategoryFallback),
  );

  // The winning offer is chosen by `total` before any arm is read, so an experiment changes what
  // is shown, never which offer wins. `displayedCandidate` is the leg pricing itself when closed.
  const { displayedCandidate, isCategoryPrice } = selectTripItemCandidate({
    pricing: pricingKey,
    passengerTypes,
    typesToFilter,
    showFlatFare,
    useFlatFareAsResultPrice: features.USE_FLAT_FARE_AS_RESULT_PRICE,
    isClosed,
  });

  /* Prices */
  // INS-1288: when a tenant hides the convenience fee, the backend ships the total without it
  // as a sibling `displayTotal` and leaves `total` alone. Which of the two this user reads is
  // an A/B assignment, resolved once here and reused for every figure on the card so one card
  // can never mix arms. Evaluated only when there is a `displayTotal` to show, so users who
  // could not tell the arms apart are never bucketed.
  //
  // Gated on the candidate that is actually rendered, not on `pricingKey`: when the winning
  // offer carries no `displayTotal` (a provider discount, which the backend never nets) the card
  // must stay entirely gross rather than pair a gross headline with a netted crossed-out price.
  const showPriceWithoutFees = usePriceWithoutFeesVariant(hasDisplayTotal(displayedCandidate));
  // BOOK-168: the markup-inclusive ("final") price, gated on the same rendered candidate. Its rule
  // refuses to run under INS-1288 (a `displayTotal`, or the tenant flag) and on exchanges, and
  // evaluates GrowthBook only when this candidate's companion is above its total, so a card that
  // would look the same in both arms never logs an exposure.
  //
  // The card's own pricing must carry one too: the previous price, table fare and discount are
  // read off it, and pricing the list swapped in (a coupon or wallet quote) arrives without
  // companions, which would pair a final headline with a base crossed-out price.
  const showFinalPrice = useFinalPriceExperiment({
    hasMarkup: hasMarkupAboveTotal(displayedCandidate) && hasMarkupAboveTotal(pricingKey),
    hasDisplayTotal: hasDisplayTotal(displayedCandidate),
    isExchange: Boolean(isExchange),
  });
  // One arm for the whole card; INS-1288 wins should both ever answer true.
  const priceArm = resolvePriceArm({ netted: showPriceWithoutFees, final: showFinalPrice });
  // The arm a pick of this card reports, so the pick never reads GrowthBook on a pricing the card
  // did not paint: a coupon or wallet quote keeps the card in control even when `trip.pricing`
  // carries companions.
  const paintsFinal = priceArm === PRICE_ARMS.FINAL;
  // The previous price, table fare and provider discount arrive from TripList on the control
  // scale; the arm's figures replace them in `cardOverrides`. A closed card receives none of them.
  const {
    totalBeforeDiscount: listTotalBeforeDiscount,
    officePrice: listOfficePrice,
    providerDiscount: listProviderDiscount,
  } = props;
  const { priceTotal, windowTicketPrice, cardOverrides } = resolveTripItemPrices({
    pricing: pricingKey,
    displayedCandidate,
    arm: priceArm,
    showTotalBase: Boolean(isLogged || isExchange),
    listFigures: {
      totalBeforeDiscount: listTotalBeforeDiscount,
      officePrice: listOfficePrice,
      providerDiscount: listProviderDiscount,
    },
  });

  /**
   * Function to handle selecting a trip item.
   * Forwards the exact pricing block this card rendered (`pricingKey`) as optional analytics
   * context, so trip-selection analytics describes the offer the customer actually saw, and the
   * arm it painted, so the pick reports that arm instead of reading it again (see `isFinal` in
   * `ProviderDepartures.selectTripDefault`).
   */
  const onSelectTrip = () => {
    selectTrip(trip, position, undefined, { renderedPricing: pricingKey, isFinal: paintsFinal });
    deleteExpiredRecentSeenTrips();
  };

  /**
   * Handles the selection of a trip item, showing the bus diagram if applicable.
   */
  const handleOnSelect = () => {
    const isRedirect = features.TRIP_REDIRECTION_LEAD && trip.redirectTo;

    const canShowBusDiagram =
      isSeatsOnResults && !isRedirect && !trip.hasConnections && !departureTrip?.hasConnections;

    if (canShowBusDiagram) {
      if (showBusDiagram) {
        cleanPrevRecentSeenTrip();
      }
      setShowBusDiagram((prevState) => !prevState);
      saveCacheRecentSeenTrip(trip.id);
      return;
    }

    if (features.FLAT_FARE_ENABLED && way === 'return') {
      if (
        flatFareValid === 'noExists' ||
        flatFareValid === 'valid' ||
        rewardTicketStatus.status === 'continue'
      ) {
        onSelectTrip();
      }
      if (flatFareValid === 'invalid') {
        setRewardTicketStatus({
          status: 'invalid',
          onAccept: () => onSelectTrip(),
        });
      }
    } else {
      onSelectTrip();
    }
  };

  // Only show provider discount advice when displaying general category price
  // If specific categories are filtered (student, older, etc.), the discount doesn't apply to them
  const shouldShowProviderDiscount = shouldShowProviderDiscountForSearch(
    searchPassengers,
    passengerTypes,
    Boolean(categoryBasedPricing),
    Boolean(halfPriceCategoryFallback),
  );

  const isRoundTripContext = isRoundTrip || (way === 'return' && Boolean(departureTrip));
  // Quick Purchase: visibility and handler
  const {
    eligible: baseEligible,
    eligibleWithoutPassengers,
    mergedPassengerCount,
  } = getBaseEligibility({
    quickPurchaseEnabled,
    searchPassengers,
    frequentPassengers,
    features,
    isRoundTrip: isRoundTripContext,
    isExchange,
    isBulkTicket,
    isQuickPurchaseLoyaltySettled,
    userIsLoggedInWithAnyLoyaltyProgram,
  });
  const showQuickPurchase = getTripEligibility(trip, baseEligible, features);

  /**
   * Handles the opening of quick purchase. In modal or screen variation.
   * @param {object} params - Quick purchase params.
   * @param {string} params.passengersParam - Passenger selection param.
   * @param {Array<string>} params.seatNumbers - Selected seat numbers.
   * @param {string} params.source - Source of the quick purchase.
   * @param {Array<object>} [params.selectedSeatReferences] - Ordered full seat references.
   */
  const openOrNavigateToQuickPurchase = ({
    passengersParam,
    seatNumbers,
    source,
    selectedSeatReferences,
  }) => {
    const departureId = trip.id;
    const returnId = departureTrip?.id || '';
    // The per-adult seen price of the purchase this creates: the trip's one-way total, or its
    // markup-inclusive companion in the final arm (BOOK-177), never INS-1288's reduced display
    // total. The arm is read here, at the click, gated on `trip.pricing` rather than the card's
    // gate (BOOK-177 design D1). The modal, the route and quick checkout carry it as it is.
    const isFinal = reportsFinalPrice([trip], { isExchange: Boolean(isExchange) });
    const seenPrice = finalPrice(trip.pricing, isFinal) || '';

    if (onQuickPurchaseRequest && quickCheckoutVariant === 'modal') {
      onQuickPurchaseRequest({
        departureId,
        returnId,
        passengersParam,
        seenPrice,
        seatNumbers,
        source,
        selectedSeatReferences,
      });
      return;
    }

    // Quick purchase is created after this route transition, so the search coupon
    // must travel in the URL to become external_coupon_code in the purchase payload.
    const path = buildQuickPurchasePath({
      departureId,
      returnId,
      passengersParam,
      seenPrice,
      seatNumbers,
      couponCode,
    });
    pushQuickPurchasePath(history, path, selectedSeatReferences);
  };

  // Quick Purchase from seat selection: uses selected seat count instead of search passengers
  const canShowQuickPurchaseInSeats = getInSeatsEligibility(
    trip,
    eligibleWithoutPassengers,
    features,
  );
  const frequentPassengersCount = mergedPassengerCount;

  /**
   * Sends the user to quick purchase from the search result.
   */
  const handleQuickPurchase = () => {
    quickPurchaseSelectedTracker('schedule', Boolean(trip.recommendationConcept));
    const passengersParam = searchPassengers.passengers || 'A1';

    openOrNavigateToQuickPurchase({
      passengersParam,
      source: 'schedule',
    });
  };

  /**
   * Sends the user to quick purchase from the seat selection.
   */
  const handleQuickPurchaseFromSeats = (seatCount, seatNumbers, selectedSeatReferences) => {
    quickPurchaseSelectedTracker('seats', Boolean(trip.recommendationConcept));
    const passengersParam = `A${seatCount}`;

    openOrNavigateToQuickPurchase({
      passengersParam,
      seatNumbers,
      source: 'seats',
      selectedSeatReferences,
    });
  };

  const itemProps = {
    ...props,
    tripId: id,
    hasFilters,
    useFlatFare: flatFareValid,
    showNewFiltersList,
    walletPoints,
    discountBase,
    isExchange,
    onSelectClick: handleOnSelect,
    showWalletPoints: resultPriceToShow.priceType === 'wallet',
    walletType: resultPriceToShow.walletType,
    total: priceTotal,
    // `totalBase` (logged-in and exchange users), the previous price, the table fare and the
    // discount amount, on the scale of `total` above; see `resolveTripItemPrices`.
    ...cardOverrides,
    windowTicketPrice,
    highLightType: trip.recommendationConcept,
    highlightText: `${t(`trips:recommended_trips.${decamelize(trip.recommendationType || '')}`)}`,
    sparkleBadgeLabel: trip.recommendationConcept
      ? t('trips:recommended_trips.badge_label')
      : undefined,
    highLightAsMobile,
    loadingPrice: couponLoading,
    recentView: isRecentlySeen,
    isSeatsOnResults,
    shouldShowProviderDiscount,
    isCategoryPrice,
    isNewUser: isNewUserForDigitalDiscount,
    pricing: pricingKey,
  };

  if (!isFromOpenTicketList && itemProps.trip && itemProps.trip.openTicket) {
    return null;
  }

  // TODO: Remove this when the AB test is finished and the config is set with the correct values
  const resultsForAb = {
    'berlinas': 'simpleResult',
    'rochoa': 'simpleResult',
    'roll-bits': 'simpleResult',
    'gfa': 'simpleResult',
    'etn': 'result',
    'gho': 'result',
    'costaline': 'result',
    'brasilia': 'result',
    'default': features.SCHEDULE_RESULT_TYPE,
  };

  let resultType = features.SCHEDULE_RESULT_TYPE;
  if (isNewResultsDesign) {
    resultType = resultsForAb[env.brand] || resultsForAb.default;
  }

  const shouldShowBusDiagram = Boolean(showBusDiagram && busDiagramEnabled);
  const isBusDiagramOpen = shouldShowBusDiagram && isDesktop;
  const isBusDiagramOpenMobile = shouldShowBusDiagram && !isDesktop;

  const scheduleBox = {
    default: <ResultDefault {...itemProps} />,
    GFA: <ResultGfa {...itemProps} />,
    specifics: <ResultSpecifics {...itemProps} />,
    horizontal: (
      <>
        <Visibility type="hideForMobileOnly">
          <ResultBoxHorizontal {...itemProps} />
        </Visibility>
        <Visibility type="showForMobileOnly">
          <ResultSpecifics {...itemProps} />
        </Visibility>
      </>
    ),
    icons: <ResultWithIcons {...itemProps} />,
    result: (
      <TripResult
        {...itemProps}
        isOpen={isBusDiagramOpen}
        showQuickPurchase={showQuickPurchase && !isBusDiagramOpen}
        onQuickPurchaseClick={handleQuickPurchase}
      >
        <SeatSelectorFactory
          way={way}
          trip={trip}
          fromTrip={departureTrip}
          isPurchaseView={false}
          finishSeatsSelection={onSelectTrip}
          resultsLayout
          isRoundTrip={isRoundTripContext}
          fromExchange={fromExchange}
          onQuickPurchaseFromSeats={
            canShowQuickPurchaseInSeats ? handleQuickPurchaseFromSeats : null
          }
          frequentPassengersCount={frequentPassengersCount}
        />
      </TripResult>
    ),
    simpleResult: (
      <SimpleTripResult
        {...itemProps}
        isOpen={isBusDiagramOpen}
        showQuickPurchase={showQuickPurchase && !isBusDiagramOpen}
        onQuickPurchaseClick={handleQuickPurchase}
      >
        <SeatSelectorFactory
          way={way}
          trip={trip}
          fromTrip={departureTrip}
          isPurchaseView={false}
          finishSeatsSelection={onSelectTrip}
          resultsLayout
          isRoundTrip={isRoundTripContext}
          fromExchange={fromExchange}
          onQuickPurchaseFromSeats={
            canShowQuickPurchaseInSeats ? handleQuickPurchaseFromSeats : null
          }
          frequentPassengersCount={frequentPassengersCount}
        />
      </SimpleTripResult>
    ),
  };

  switch (itemProps.type || type) {
    case 'bus':
      content = scheduleBox[resultType] || <ResultDefault {...itemProps} />;
      break;
    case 'flight':
      content = <FlightItem {...itemProps} />;
      break;
    case 'mixed':
      content = <MixedItem {...itemProps} />;
      break;
    default:
      content = <div />;
  }

  const { enabled: lastSeatsEnabled, quantity: lastSeatsMin } = features.LAST_SEATS_MESSAGE || {};
  const lastSeatMessages =
    lastSeatsEnabled && availability <= lastSeatsMin ? (
      <Text color="white" size="S" weight="semibold">
        {t('trips:label.last_seats_available')} 📢
      </Text>
    ) : null;

  let notice = lastSeatMessages;
  let noticePosition = false;
  let noticeBg = false;

  const dotersMessage = (
    <Gap alignItems="center">
      <Text color="white" size="S">
        {t('purchase:label.reward_ticket', { ...(isRoundTrip && { context: way }) })}
      </Text>
      <Icon type={dotersDot} size="S" />
    </Gap>
  );

  const noValidFlatFare = (
    <Gap alignItems="center">
      <Text color="grayStrong" size="S">
        {t('purchase:label.reward_ticket_invalid', { context: features.FLAT_FARE_CONTEXT })}
      </Text>
    </Gap>
  );

  if (flatFareValid && !isExchange) {
    if (flatFareValid === 'valid') notice = dotersMessage;
    if (flatFareValid === 'invalid') notice = noValidFlatFare;
    if (flatFareValid === 'valid' || flatFareValid === 'invalid') noticePosition = 'right';
    if (flatFareValid === 'valid') noticeBg = 'grayStrong';
    if (flatFareValid === 'invalid') noticeBg = 'warning';
  }

  const withoutResultBox =
    isNewResultsDesign === true || resultType === 'result' || resultType === 'simpleResult';

  // Apply disabled styling for closed trips
  const closedTripClasses = isClosed ? 'opacity-60 pointer-events-none' : '';
  const terminalGroupClasses = isTerminalGrouped
    ? 'recommended-trip-container--terminal-group'
    : '';
  const containerClasses = trip.recommendationConcept
    ? `recommended-trip-container ${terminalGroupClasses} ${closedTripClasses}`
    : closedTripClasses;

  return withoutResultBox ? (
    <>
      <div className={containerClasses}>{content}</div>
      {isBusDiagramOpenMobile && !isClosed && (
        <SeatSelectorFactory
          onCloseModal={() => setShowBusDiagram(false)}
          trip={trip}
          way={way}
          history={history}
          finishSeatsSelection={onSelectTrip}
          fromTrip={departureTrip}
          isPurchaseView={false}
          resultsLayout
          isRoundTrip={isRoundTripContext}
          onQuickPurchaseFromSeats={
            canShowQuickPurchaseInSeats ? handleQuickPurchaseFromSeats : null
          }
          frequentPassengersCount={frequentPassengersCount}
        />
      )}
    </>
  ) : (
    <div className={closedTripClasses}>
      <ResultBox
        onClick={isNewResultsDesign === false || isClosed ? null : handleOnSelect}
        notice={notice}
        noticePosition={noticePosition}
        noticeBg={noticeBg}
        flat={resultBoxStyle === 'flat'}
        outline={resultBoxStyle === 'outline'}
      >
        {content}
      </ResultBox>
    </div>
  );
};

TripItem.propTypes = {
  // Gross previous price from TripList; netted alongside `total` when the tenant hides fees, read
  // from the markup companions in the final-price arm.
  totalBeforeDiscount: PropTypes.number,
  // Gross window price from TripList, crossed out under SHOW_EARLY_DISCOUNTS; netted the same way,
  // shifted by the markup in the final-price arm.
  officePrice: PropTypes.number,
  // Applied provider discount from TripList; its amount is read from the companions in the
  // final-price arm, so a derived percentage never mixes arms.
  providerDiscount: PropTypes.shape({ amount: PropTypes.number, percent: PropTypes.number }),
  trip: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  selectTrip: PropTypes.func.isRequired,
  isFromOpenTicketList: PropTypes.bool,
  type: PropTypes.string,
  availability: PropTypes.number.isRequired,
  filtersApplied: PropTypes.object,
  isExchange: PropTypes.bool,
  exchangePassengers: PropTypes.array,
  isRoundTrip: PropTypes.bool,
  way: PropTypes.string.isRequired,
  isLogged: PropTypes.bool,
  departureTrip: PropTypes.object,
  rewardTicketStatus: PropTypes.object,
  setRewardTicketStatus: PropTypes.func,
  total: PropTypes.number.isRequired,
  highLightAsMobile: PropTypes.bool,
  allowsSeatSelection: PropTypes.bool,
  isClosed: PropTypes.bool, // If true, applies disabled styling for closed trips
  isTerminalGrouped: PropTypes.bool,
  onQuickPurchaseRequest: PropTypes.func,
  pricing: PropTypes.object, // Applicable pricing block already resolved by the list
};

TripItem.defaultProps = {
  type: 'bus',
  onQuickPurchaseRequest: null,
};

export default TripItem;
